import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Newspaper, ExternalLink, Filter, Calendar, Archive as ArchiveIcon, Star, BadgeCheck, MapPin, Globe, Languages, ChevronDown, X } from "lucide-react";
import { useCountry } from "@/contexts/CountryContext";
import NoIndex from "@/components/NoIndex";

// /things-to-know carries aggregated SocialPulse news. On the
// IndianFamilyTree.com build the 2026-10-06 SEO audit flagged this as
// off-topic shared content that bleeds into IFT search results, so we
// noindex it there. Other sites continue to index normally.
const _NOINDEX_ON_IFT = (process.env.REACT_APP_SITE_SOURCE || "").toLowerCase() === "indianfamilytree";
import { useLocality } from "@/contexts/LocalityContext";
import { newsImageFor } from "@/lib/newsFallback";
import TranslatableText from "@/components/TranslatableText";
import DailyBriefingPlayer from "@/components/DailyBriefingPlayer";
import DigestOptIn from "@/components/DigestOptIn";
import {
  PAGE_TRANSLATE_KEY,
  PAGE_TRANSLATE_EVENT,
  PAGE_REVERT_EVENT,
} from "@/components/PageTranslateBar";

// Curated language list — same set the PageTranslateBar exposes. Kept
// separate here so the News page can offer a compact inline picker.
const NEWS_LANGS = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish · Español" },
  { code: "fr", label: "French · Français" },
  { code: "de", label: "German · Deutsch" },
  { code: "pt", label: "Portuguese · Português" },
  { code: "it", label: "Italian · Italiano" },
  { code: "ru", label: "Russian · Русский" },
  { code: "hi", label: "Hindi · हिन्दी" },
  { code: "mr", label: "Marathi · मराठी" },
  { code: "bn", label: "Bengali · বাংলা" },
  { code: "ta", label: "Tamil · தமிழ்" },
  { code: "te", label: "Telugu · తెలుగు" },
  { code: "ml", label: "Malayalam · മലയാളം" },
  { code: "kn", label: "Kannada · ಕನ್ನಡ" },
  { code: "gu", label: "Gujarati · ગુજરાતી" },
  { code: "pa", label: "Punjabi · ਪੰਜਾਬੀ" },
  { code: "ur", label: "Urdu · اردو" },
  { code: "ar", label: "Arabic · العربية" },
  { code: "zh", label: "Chinese · 中文" },
  { code: "ja", label: "Japanese · 日本語" },
  { code: "ko", label: "Korean · 한국어" },
  { code: "th", label: "Thai · ไทย" },
  { code: "vi", label: "Vietnamese · Tiếng Việt" },
  { code: "id", label: "Indonesian · Bahasa Indonesia" },
  { code: "tr", label: "Turkish · Türkçe" },
  { code: "sw", label: "Swahili · Kiswahili" },
];

const CATEGORIES = [
  "All", "Weather", "War", "Disasters", "News You Can Use",
  "World", "Politics", "Business", "Technology", "Science", "Health",
  "Environment", "Entertainment", "Sports", "Good News", "Odd News",
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function Stars({ n }) {
  return (
    <span className="inline-flex items-center gap-0.5" title={`Importance ${n}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={11}
          strokeWidth={2}
          className={i <= n ? "" : "opacity-25"}
          style={{ color: i <= n ? "var(--wofy-primary)" : "#999", fill: i <= n ? "var(--wofy-primary)" : "transparent" }}
        />
      ))}
    </span>
  );
}

function NewsCard({ a }) {
  return (
    <article
      data-testid={`news-card-${a.id}`}
      className="grid grid-cols-[120px_1fr] gap-4 py-5 border-b"
      style={{ borderColor: "var(--wofy-line)" }}
    >
      <a href={a.url} target="_blank" rel="noopener noreferrer" className="block">
        <div
          className="w-[120px] h-[120px] rounded-md overflow-hidden bg-[var(--wofy-card-bg,#f2ede4)] flex items-center justify-center"
          style={{ border: "1px solid var(--wofy-line)" }}
        >
          <img
            src={newsImageFor(a)}
            alt=""
            className="w-full h-full object-cover"
            data-testid={`news-thumb-${a.id}`}
            onError={(e) => {
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = "1";
                e.currentTarget.src = "/news-fallbacks/default.svg";
              }
            }}
          />
        </div>
      </a>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span
            className="text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded"
            style={{ background: "var(--wofy-primary)", color: "white" }}
          >
            {a.category}
          </span>
          <Stars n={a.importance} />
          {a.pinned && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded" style={{ background: "#c47f3a", color: "white" }}>
              Pinned
            </span>
          )}
          <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            {a.source_name}
            {a.author_trusted && (
              <BadgeCheck className="inline w-3.5 h-3.5 -mt-0.5 ml-1" style={{ color: "#1D4ED8" }} aria-label="Trusted publisher" />
            )}
            {" · "}{timeAgo(a.published_at)}
          </span>
        </div>
        <Link to={`/things-to-know/${a.id}`} className="block">
          <h3 className="font-display text-xl md:text-2xl leading-snug mb-2 hover:underline">
            {a.title}
          </h3>
        </Link>
        <TranslatableText
          text={a.ai_summary}
          testid={`news-summary-${a.id}`}
          minChars={30}
          className="text-sm md:text-base leading-relaxed mb-3"
        />
        {a.related_urls && a.related_urls.length > 0 && (
          <div data-testid={`news-related-${a.id}`} className="flex items-center gap-1.5 flex-wrap mb-3 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            <span className="uppercase tracking-wider text-[10px] font-semibold">Also reported by:</span>
            {a.related_urls.slice(0, 5).map((r, i) => (
              <a
                key={i}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2 py-0.5 rounded-full border hover:opacity-80"
                style={{ borderColor: "var(--wofy-line)" }}
              >
                {r.source_name || "Publisher"} <ExternalLink size={9} className="inline -mt-0.5" />
              </a>
            ))}
            {a.related_urls.length > 5 && (
              <span className="opacity-60">+{a.related_urls.length - 5} more</span>
            )}
          </div>
        )}
        {(a.state_province || a.city) && (
          <div className="mb-3 text-[10px] uppercase tracking-wider font-semibold inline-block px-2 py-0.5 rounded-full border" style={{ borderColor: "var(--wofy-line)", color: "var(--wofy-text-soft)" }}>
            📍 {[a.city, a.state_province].filter(Boolean).join(", ")}
          </div>
        )}
        <div className="flex items-center gap-3 text-sm">
          <a
            data-testid={`news-read-original-${a.id}`}
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold"
            style={{ background: "var(--wofy-primary)", color: "white" }}
          >
            Read Original <ExternalLink size={13} />
          </a>
          <Link to={`/things-to-know/${a.id}`} className="text-sm underline" style={{ color: "var(--wofy-primary)" }}>
            View brief
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function ThingsToKnow() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ curated: 0, recent: 0, location_fallback: null });
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const { code, country } = useCountry();
  const { locality } = useLocality();
  // Auto-apply the user's saved country filter on first render — they can
  // still turn it off with the checkbox below if they want the global mix.
  const [countryFilterOn, setCountryFilterOn] = useState(!!code);
  // Optional deeper filter: only show stories tagged with the user's state.
  const [stateFilterOn, setStateFilterOn] = useState(!!(locality?.state));
  // News-only language picker — English by default. Dispatches the same
  // page-translate event so all NewsCards translate in place.
  const [newsLang, setNewsLang] = useState(() => {
    try { return localStorage.getItem(PAGE_TRANSLATE_KEY) || "en"; } catch { return "en"; }
  });
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  // If the user picks/clears a country from the header while on this page,
  // mirror that into the toggle so the feed instantly reflects the change.
  useEffect(() => { setCountryFilterOn(!!code); }, [code]);
  useEffect(() => { setStateFilterOn(!!(locality?.state)); }, [locality?.state]);

  const load = () => {
    setLoading(true);
    const params = { limit: 60 };
    if (category !== "All") params.category = category;
    if (countryFilterOn && code && country?.name) params.country = country.name;
    else if (countryFilterOn && locality?.country) params.country = locality.country;
    if (stateFilterOn && locality?.state) params.state = locality.state;
    http.get("/news", { params })
      .then((r) => {
        setItems(r.data.items || []);
        setMeta({
          curated: r.data.curated || 0,
          recent: r.data.recent || 0,
          location_fallback: r.data.location_fallback || null,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, countryFilterOn, stateFilterOn, code, locality?.state, locality?.country]);

  const dateLabel = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  }, []);

  return (
    <div data-testid="things-to-know-page" className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
      {_NOINDEX_ON_IFT && <NoIndex />}
      <div className="mb-8">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
          <Newspaper size={14} className="inline mr-1.5 -mt-0.5" />
          THINGS TO KNOW
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight">Your Daily Briefing.</h1>
            <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>
              <Calendar size={13} className="inline -mt-0.5 mr-1" />
              {dateLabel} · {items.length} stories{meta.curated ? ` (${meta.curated} curated Top ${meta.curated}${meta.recent ? ` + ${meta.recent} recent` : ""})` : ""} · AI-summarised · Click to read the full article on the publisher.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              data-testid="things-to-know-submit-link"
              to="/things-to-know/submit"
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full font-semibold"
              style={{ background: "var(--wofy-primary)", color: "white" }}
            >
              <Newspaper size={14} /> Submit News
            </Link>
            <Link
              data-testid="things-to-know-archive-link"
              to="/things-to-know/archive"
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border"
              style={{ borderColor: "var(--wofy-line)" }}
            >
              <ArchiveIcon size={14} /> Archive
            </Link>
            <Link
              data-testid="things-to-know-nature-link"
              to="/nature"
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border"
              style={{ borderColor: "var(--wofy-line)" }}
            >
              🌦 Weather & Nature
            </Link>
          </div>
        </div>

        <Link
          to="/articles"
          data-testid="ttk-articles-cta"
          className="mt-6 flex items-center justify-between gap-4 rounded-2xl p-5 border transition-shadow hover:shadow-lg group"
          style={{
            borderColor: "var(--wofy-primary)",
            background: "linear-gradient(135deg, #FEF3C7 0%, #FFE4E6 100%)",
          }}
        >
          <div className="max-w-2xl">
            <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: "var(--wofy-primary)" }}>
              READ · SOCIAL PULSE EDITORIAL
            </div>
            <div className="font-display text-xl md:text-2xl leading-snug" style={{ color: "var(--wofy-text)" }}>
              Long-form articles: travel guides, airlines &amp; airports, countries and community stories.
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              Curated by our editorial team — a deeper read beyond the daily briefing.
            </div>
          </div>
          <span
            className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium shadow-sm shrink-0 group-hover:translate-x-0.5 transition-transform"
            style={{ background: "var(--wofy-primary)", color: "white" }}
          >
            Browse Articles →
          </span>
        </Link>
      </div>

      {/* Filters */}
      <DigestOptIn />

      {/* Show Local News + language picker strip. Bold visual anchor so
          users always spot it. Toggles the country filter on/off and lets
          them pick a translation language for all news cards on the page. */}
      <div
        data-testid="show-local-news-strip"
        className="rounded-2xl border p-4 mb-6 flex flex-wrap items-center gap-3"
        style={{
          borderColor: "var(--wofy-primary)",
          background: "linear-gradient(135deg, #FEF3C7 0%, #FFE4E6 100%)",
        }}
      >
        <span className="inline-flex items-center gap-2 font-semibold" style={{ color: "var(--wofy-primary)" }}>
          <MapPin className="w-4 h-4" />
          <span className="uppercase tracking-wider text-xs">Show Local News</span>
        </span>
        <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
          <input
            data-testid="show-local-news-toggle"
            type="checkbox"
            checked={countryFilterOn}
            onChange={(e) => setCountryFilterOn(e.target.checked)}
            className="w-4 h-4 accent-[var(--wofy-primary)]"
          />
          <span>
            {countryFilterOn
              ? <>Showing stories from <b>{country?.name || locality?.country || "your country"}</b></>
              : <>Show only stories from your country</>}
          </span>
        </label>
        {locality?.state && (
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
            <input
              data-testid="show-local-news-state-toggle"
              type="checkbox"
              checked={stateFilterOn}
              onChange={(e) => setStateFilterOn(e.target.checked)}
              className="w-4 h-4 accent-[var(--wofy-primary)]"
            />
            <span>Narrow to <b>{locality.state}</b></span>
          </label>
        )}

        {/* News-only language picker. English by default; picking a new
            language broadcasts the page-translate event so cards translate. */}
        <div className="ml-auto flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-xs uppercase font-semibold" style={{ color: "var(--wofy-primary)" }}>
            <Languages className="w-3.5 h-3.5" /> Read in
          </span>
          <div className="relative">
            <button
              type="button"
              onClick={() => setLangMenuOpen((s) => !s)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-white text-sm font-semibold hover:shadow-sm"
              style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" }}
              data-testid="news-lang-picker-btn"
              aria-haspopup="listbox"
              aria-expanded={langMenuOpen}
            >
              {(NEWS_LANGS.find((l) => l.code === newsLang) || NEWS_LANGS[0]).label}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {langMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setLangMenuOpen(false)} />
                <div
                  className="absolute right-0 mt-1 z-50 rounded-xl shadow-xl border text-sm w-64 overflow-y-auto"
                  style={{ background: "#FDFBF7", borderColor: "var(--wofy-border)", maxHeight: "60vh" }}
                  data-testid="news-lang-menu"
                  role="listbox"
                >
                  {NEWS_LANGS.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      role="option"
                      aria-selected={l.code === newsLang}
                      onClick={() => {
                        setNewsLang(l.code);
                        setLangMenuOpen(false);
                        if (l.code === "en") {
                          try { localStorage.removeItem(PAGE_TRANSLATE_KEY); } catch { /* ignore */ }
                          try { window.dispatchEvent(new CustomEvent(PAGE_REVERT_EVENT)); } catch { /* ignore */ }
                        } else {
                          try { localStorage.setItem(PAGE_TRANSLATE_KEY, l.code); } catch { /* ignore */ }
                          try { window.dispatchEvent(new CustomEvent(PAGE_TRANSLATE_EVENT, { detail: { lang: l.code } })); } catch { /* ignore */ }
                        }
                      }}
                      className="w-full text-left px-3 py-1.5 hover:bg-black/[0.04]"
                      style={{ background: "#FDFBF7" }}
                      data-testid={`news-lang-option-${l.code}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {newsLang !== "en" && (
            <button
              type="button"
              onClick={() => {
                setNewsLang("en");
                try { localStorage.removeItem(PAGE_TRANSLATE_KEY); } catch { /* ignore */ }
                try { window.dispatchEvent(new CustomEvent(PAGE_REVERT_EVENT)); } catch { /* ignore */ }
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold bg-white hover:shadow-sm"
              style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" }}
              data-testid="news-lang-reset"
              aria-label="Reset to English"
              title="Reset to English"
            >
              <X className="w-3 h-3" /> English
            </button>
          )}
        </div>
      </div>

      {/* Podcast-style Daily Briefing player — reads top 5 aloud in queue. */}
      <DailyBriefingPlayer items={items} max={5} />

      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <Filter size={14} style={{ color: "var(--wofy-text-soft)" }} />
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`news-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => setCategory(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide border ${category === c ? "" : "hover:opacity-70"}`}
            style={{
              borderColor: "var(--wofy-line)",
              background: category === c ? "var(--wofy-primary)" : "transparent",
              color: category === c ? "white" : "var(--wofy-text)",
            }}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="flex gap-4 mb-6 text-sm items-center flex-wrap">
        {meta.curated > 0 && (
          <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            Top {meta.curated} today · then {meta.recent} more from the last 48 h
          </span>
        )}
      </div>
      {meta.location_fallback === "dropped_state" && locality?.state && (
        <div data-testid="news-location-fallback" className="mb-4 text-xs rounded-lg px-3 py-2 border" style={{ borderColor: "var(--wofy-line)", background: "#FEF3C7", color: "#92400E" }}>
          No stories tagged <b>{locality.state}</b> yet — showing country-level stories instead. New articles get tagged with state/city as they're ingested.
        </div>
      )}
      {meta.location_fallback === "dropped_city" && (
        <div data-testid="news-location-fallback" className="mb-4 text-xs rounded-lg px-3 py-2 border" style={{ borderColor: "var(--wofy-line)", background: "#FEF3C7", color: "#92400E" }}>
          No stories tagged with your city yet — showing state / country stories instead.
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center" style={{ color: "var(--wofy-text-soft)" }}>Loading today's briefing…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center border rounded-lg" style={{ borderColor: "var(--wofy-line)", color: "var(--wofy-text-soft)" }}>
          <p>{category !== "All" ? `No stories in the "${category}" category right now.` : "No stories to show yet — our feed is refreshing."}</p>
          <p className="mt-2 text-xs">
            {category !== "All" && <button onClick={() => setCategory("All")} className="underline mr-3" style={{ color: "var(--wofy-primary)" }}>Show all categories</button>}
            {countryFilterOn && <button onClick={() => setCountryFilterOn(false)} className="underline" style={{ color: "var(--wofy-primary)" }}>Clear country filter</button>}
          </p>
        </div>
      ) : (
        <div>
          {items.map((a) => (<NewsCard key={a.id} a={a} />))}
        </div>
      )}
    </div>
  );
}
